import { useState } from "react";
import { authAxios as ax } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, UserPlus, Pencil, Archive, Users as UsersIcon, Plug, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const langBadgeColors = {
  en: "bg-[#002FA7]/10 text-[#002FA7]",
  es: "bg-[#0A0A0A]/10 text-[#0A0A0A]",
  fr: "bg-[#00C853]/10 text-[#00C853]",
  ht: "bg-[#FFB800]/10 text-[#FFB800]",
};
const ALL_LANGS = ["en", "es", "fr", "ht"];

export default function ProjectsTab({ t, projects, operators, reload, user }) {
  const [newProject, setNewProject] = useState({
    name: "", client_name: "", description: "", languages: ["en"], twilio_number: "",
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [assignDialog, setAssignDialog] = useState({ open: false, project: null });
  const [selectedOps, setSelectedOps] = useState([]);
  const [crmDialog, setCrmDialog] = useState({ open: false, project: null });
  const [crmConfig, setCrmConfig] = useState({
    enabled: false,
    type: "zendesk",
    sf: { base_url: "https://login.salesforce.com", client_id: "", client_secret: "", username: "", password: "", security_token: "", api_version: "v60.0" },
    zd: { subdomain: "", email: "", api_token: "" },
  });
  const [crmTesting, setCrmTesting] = useState(false);
  const [crmSaved, setCrmSaved] = useState(false);

  const toggleLang = (set, setSet, lang) => {
    if (set.includes(lang)) setSet(set.filter((l) => l !== lang));
    else setSet([...set, lang]);
  };

  const createProject = async () => {
    if (!newProject.name.trim()) return toast.error("Project name is required");
    try {
      await ax.post(`${API}/admin/projects`, newProject);
      toast.success("Project created");
      setNewProject({ name: "", client_name: "", description: "", languages: ["en"], twilio_number: "" });
      setCreateOpen(false);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create project");
    }
  };

  const saveEdit = async () => {
    try {
      const payload = {
        name: editProject.name,
        client_name: editProject.client_name,
        description: editProject.description,
        languages: editProject.languages,
        twilio_number: editProject.twilio_number || "",
      };
      await ax.put(`${API}/admin/projects/${editProject.project_id}`, payload);
      toast.success("Project updated");
      setEditProject(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const archiveProject = async (p) => {
    if (!window.confirm(`Archive project "${p.name}"? Historical data is preserved.`)) return;
    try {
      await ax.delete(`${API}/admin/projects/${p.project_id}`);
      toast.success("Project archived");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Archive failed");
    }
  };

  const openAssign = async (p) => {
    try {
      const res = await ax.get(`${API}/admin/projects/${p.project_id}/operators`);
      setSelectedOps(res.data.map((o) => o.user_id));
    } catch {
      setSelectedOps([]);
    }
    setAssignDialog({ open: true, project: p });
  };

  const saveAssignments = async () => {
    try {
      await ax.post(`${API}/admin/projects/${assignDialog.project.project_id}/bulk-assign`, {
        operator_ids: selectedOps,
      });
      toast.success(`${selectedOps.length} operator(s) assigned`);
      setAssignDialog({ open: false, project: null });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Assignment failed");
    }
  };

  const openCrm = async (p) => {
    setCrmSaved(false);
    setCrmDialog({ open: true, project: p });
    try {
      const res = await ax.get(`${API}/admin/projects/${p.project_id}/crm-config`);
      const c = res.data || {};
      const creds = c.credentials || {};
      // The masked credentials are display-only — show subdomain/email/username/client_id, leave secrets blank for re-entry
      setCrmConfig({
        enabled: !!c.enabled,
        type: c.type || "zendesk",
        sf: {
          base_url: creds.base_url || "https://login.salesforce.com",
          client_id: creds.client_id || "",
          client_secret: "",
          username: creds.username || "",
          password: "",
          security_token: "",
          api_version: creds.api_version || "v60.0",
        },
        zd: {
          subdomain: creds.subdomain || "",
          email: creds.email || "",
          api_token: "",
        },
      });
    } catch {
      // fresh project, no CRM yet — keep defaults
    }
  };

  const buildCrmPayload = () => {
    const credentials = crmConfig.type === "salesforce"
      ? { ...crmConfig.sf }
      : { ...crmConfig.zd };
    return { enabled: crmConfig.enabled, type: crmConfig.type, credentials };
  };

  const saveCrm = async () => {
    try {
      await ax.put(`${API}/admin/projects/${crmDialog.project.project_id}/crm-config`, buildCrmPayload());
      setCrmSaved(true);
      toast.success("CRM config saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const testCrm = async () => {
    setCrmTesting(true);
    try {
      const res = await ax.post(`${API}/admin/projects/${crmDialog.project.project_id}/crm-test`);
      if (res.data?.ok) toast.success(`Connection OK — ${res.data.type}`);
      else toast.error("Connection failed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Connection failed");
    } finally {
      setCrmTesting(false);
    }
  };

  const canArchive = user?.role === "super-admin";

  return (
    <div className="space-y-4" data-testid="projects-tab">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold">{t("admin.project_health")}</h3>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white h-9 text-xs font-bold tracking-[0.1em] uppercase" data-testid="create-project-btn">
              <Plus size={14} className="mr-1" /> {t("admin.create_project")}
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none max-w-lg">
            <DialogHeader><DialogTitle>{t("admin.create_project")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input className="rounded-none" placeholder={t("common.name")} value={newProject.name} onChange={(e) => setNewProject({ ...newProject, name: e.target.value })} data-testid="project-name-input" />
              <Input className="rounded-none" placeholder="Client Name" value={newProject.client_name} onChange={(e) => setNewProject({ ...newProject, client_name: e.target.value })} data-testid="project-client-input" />
              <Textarea className="rounded-none" placeholder="Description" value={newProject.description} onChange={(e) => setNewProject({ ...newProject, description: e.target.value })} data-testid="project-desc-input" />
              <Input className="rounded-none" placeholder="Twilio Number (e.g. +12025550100)" value={newProject.twilio_number} onChange={(e) => setNewProject({ ...newProject, twilio_number: e.target.value })} data-testid="project-twilio-input" />
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF] mb-1 block">Languages</label>
                <div className="flex gap-2 flex-wrap">
                  {ALL_LANGS.map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => toggleLang(newProject.languages, (l) => setNewProject({ ...newProject, languages: l }), lang)}
                      className={`px-2 py-1 text-[10px] font-bold uppercase border ${newProject.languages.includes(lang) ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-[#4B5563] border-[#E5E7EB]"}`}
                      data-testid={`new-proj-lang-${lang}`}
                    >{lang}</button>
                  ))}
                </div>
              </div>
              <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white w-full h-9 text-xs font-bold tracking-[0.1em] uppercase" onClick={createProject} data-testid="project-submit-btn">{t("common.create")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-[#E5E7EB] bg-white">
        <Table>
          <TableHeader>
            <TableRow className="border-b-2 border-[#0A0A0A]/80">
              <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0A0A0A] py-3">{t("common.name")}</TableHead>
              <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0A0A0A] py-3">{t("admin.active_ops")}</TableHead>
              <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0A0A0A] py-3">{t("admin.lang_queue")}</TableHead>
              <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0A0A0A] py-3">{t("admin.sla_compliance")}</TableHead>
              <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0A0A0A] py-3">{t("admin.calls_today")}</TableHead>
              <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0A0A0A] py-3">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {projects.map((p) => (
              <TableRow key={p.project_id} className="border-b border-[#E5E7EB] hover:bg-[#F8F9FA]" data-testid={`project-row-${p.project_id}`}>
                <TableCell className="py-3">
                  <p className="font-semibold text-sm text-[#0A0A0A]">{p.name}</p>
                  <p className="text-xs text-[#9CA3AF]">{p.client_name}</p>
                  {p.twilio_number && <p className="text-[10px] text-[#002FA7] font-mono mt-0.5">{p.twilio_number}</p>}
                </TableCell>
                <TableCell className="py-3 text-sm font-bold text-[#0A0A0A]">{p.active_operators}</TableCell>
                <TableCell className="py-3">
                  <div className="flex gap-1 flex-wrap">
                    {Object.entries(p.language_queue || {}).map(([lang, count]) => (
                      <span key={lang} className={`px-1.5 py-0.5 text-[10px] font-bold uppercase ${langBadgeColors[lang] || "bg-gray-100 text-gray-600"}`}>
                        {lang}: {count}
                      </span>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="py-3">
                  <span className={`text-sm font-bold ${p.sla_compliance >= 90 ? "text-[#00C853]" : p.sla_compliance >= 80 ? "text-[#FFB800]" : "text-[#FF2A2A]"}`}>
                    {p.sla_compliance}%
                  </span>
                </TableCell>
                <TableCell className="py-3 text-sm text-[#0A0A0A]">{p.calls_today}</TableCell>
                <TableCell className="py-3">
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px] font-bold tracking-[0.1em] uppercase" onClick={() => openAssign(p)} data-testid={`assign-btn-${p.project_id}`}>
                      <UsersIcon size={12} className="mr-1" /> {t("admin.assign")}
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px] font-bold tracking-[0.1em] uppercase" onClick={() => openCrm(p)} data-testid={`crm-btn-${p.project_id}`}>
                      <Plug size={12} className="mr-1" /> CRM
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px] font-bold tracking-[0.1em] uppercase" onClick={() => setEditProject({ ...p, languages: p.languages || ["en"], twilio_number: p.twilio_number || "" })} data-testid={`edit-btn-${p.project_id}`}>
                      <Pencil size={12} />
                    </Button>
                    {canArchive && (
                      <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px] font-bold tracking-[0.1em] uppercase text-[#FF2A2A] hover:bg-[#FF2A2A]/10" onClick={() => archiveProject(p)} data-testid={`archive-btn-${p.project_id}`}>
                        <Archive size={12} />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {projects.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-6 text-sm text-[#9CA3AF]">{t("common.no_data")}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit Project Dialog */}
      <Dialog open={!!editProject} onOpenChange={(o) => !o && setEditProject(null)}>
        <DialogContent className="rounded-none max-w-lg" data-testid="edit-project-dialog">
          <DialogHeader><DialogTitle>Edit Project</DialogTitle></DialogHeader>
          {editProject && (
            <div className="space-y-3">
              <Input className="rounded-none" placeholder="Name" value={editProject.name} onChange={(e) => setEditProject({ ...editProject, name: e.target.value })} data-testid="edit-name-input" />
              <Input className="rounded-none" placeholder="Client" value={editProject.client_name || ""} onChange={(e) => setEditProject({ ...editProject, client_name: e.target.value })} data-testid="edit-client-input" />
              <Textarea className="rounded-none" placeholder="Description" value={editProject.description || ""} onChange={(e) => setEditProject({ ...editProject, description: e.target.value })} data-testid="edit-desc-input" />
              <Input className="rounded-none" placeholder="Twilio Number" value={editProject.twilio_number || ""} onChange={(e) => setEditProject({ ...editProject, twilio_number: e.target.value })} data-testid="edit-twilio-input" />
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF] mb-1 block">Languages</label>
                <div className="flex gap-2 flex-wrap">
                  {ALL_LANGS.map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => toggleLang(editProject.languages, (l) => setEditProject({ ...editProject, languages: l }), lang)}
                      className={`px-2 py-1 text-[10px] font-bold uppercase border ${editProject.languages.includes(lang) ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-[#4B5563] border-[#E5E7EB]"}`}
                      data-testid={`edit-lang-${lang}`}
                    >{lang}</button>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-bold tracking-[0.1em] uppercase" onClick={saveEdit} data-testid="edit-save-btn">Save</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Dialog */}
      <Dialog open={assignDialog.open} onOpenChange={(o) => setAssignDialog({ ...assignDialog, open: o })}>
        <DialogContent className="rounded-none max-w-md" data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle>{t("admin.resource_alloc")} — {assignDialog.project?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {operators.length === 0 && <p className="text-sm text-[#9CA3AF]">No operators available.</p>}
            {operators.map((op) => {
              const checked = selectedOps.includes(op.user_id);
              const disabled = op.status === "deactivated";
              return (
                <label key={op.user_id} className={`flex items-center gap-2 p-2 border ${checked ? "bg-[#002FA7]/5 border-[#002FA7]/30" : "bg-white border-[#E5E7EB]"} ${disabled ? "opacity-50" : "cursor-pointer"}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => {
                      if (disabled) return;
                      setSelectedOps(checked ? selectedOps.filter((id) => id !== op.user_id) : [...selectedOps, op.user_id]);
                    }}
                    data-testid={`assign-check-${op.user_id}`}
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{op.name} {disabled && <span className="text-[9px] uppercase text-[#FF2A2A]">(deactivated)</span>}</p>
                    <p className="text-[10px] text-[#9CA3AF]">{op.email}</p>
                  </div>
                  <div className="flex gap-0.5">
                    {(op.languages || []).map((l) => (
                      <Badge key={l} variant="outline" className={`rounded-none text-[9px] font-bold px-1 ${langBadgeColors[l]}`}>{l}</Badge>
                    ))}
                  </div>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white w-full h-9 text-xs font-bold tracking-[0.1em] uppercase" onClick={saveAssignments} data-testid="assign-save-btn">
              <UserPlus size={12} className="mr-1" /> Save Assignments ({selectedOps.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* CRM Configuration Dialog */}
      <Dialog open={crmDialog.open} onOpenChange={(o) => setCrmDialog({ ...crmDialog, open: o })}>
        <DialogContent className="rounded-none max-w-xl" data-testid="crm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plug size={16} /> CRM Integration — {crmDialog.project?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={crmConfig.enabled}
                onChange={(e) => setCrmConfig({ ...crmConfig, enabled: e.target.checked })}
                data-testid="crm-enabled-toggle"
              />
              <span className="font-bold text-[#0A0A0A]">Enable CRM auto-push on call completion</span>
            </label>

            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF] mb-1 block">CRM Type</label>
              <Select value={crmConfig.type} onValueChange={(v) => setCrmConfig({ ...crmConfig, type: v })} disabled={!crmConfig.enabled}>
                <SelectTrigger className="rounded-none" data-testid="crm-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="zendesk">Zendesk</SelectItem>
                  <SelectItem value="salesforce">Salesforce</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {crmConfig.type === "zendesk" && (
              <div className="space-y-2 p-3 bg-[#F8F9FA] border border-[#E5E7EB]">
                <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0A0A0A]">Zendesk credentials</p>
                <Input className="rounded-none" placeholder="Subdomain (e.g. talk2support)" value={crmConfig.zd.subdomain} onChange={(e) => setCrmConfig({ ...crmConfig, zd: { ...crmConfig.zd, subdomain: e.target.value } })} disabled={!crmConfig.enabled} data-testid="zd-subdomain" />
                <Input className="rounded-none" placeholder="Admin email" value={crmConfig.zd.email} onChange={(e) => setCrmConfig({ ...crmConfig, zd: { ...crmConfig.zd, email: e.target.value } })} disabled={!crmConfig.enabled} data-testid="zd-email" />
                <Input className="rounded-none" type="password" placeholder="API token" value={crmConfig.zd.api_token} onChange={(e) => setCrmConfig({ ...crmConfig, zd: { ...crmConfig.zd, api_token: e.target.value } })} disabled={!crmConfig.enabled} data-testid="zd-api-token" />
                <p className="text-[9px] text-[#9CA3AF]">Get an API token at: Admin → Apps and integrations → Zendesk API → Settings.</p>
              </div>
            )}

            {crmConfig.type === "salesforce" && (
              <div className="space-y-2 p-3 bg-[#F8F9FA] border border-[#E5E7EB]">
                <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0A0A0A]">Salesforce OAuth2 (Password Flow)</p>
                <Input className="rounded-none" placeholder="Login URL (default https://login.salesforce.com)" value={crmConfig.sf.base_url} onChange={(e) => setCrmConfig({ ...crmConfig, sf: { ...crmConfig.sf, base_url: e.target.value } })} disabled={!crmConfig.enabled} data-testid="sf-base-url" />
                <Input className="rounded-none" placeholder="Consumer Key (client_id)" value={crmConfig.sf.client_id} onChange={(e) => setCrmConfig({ ...crmConfig, sf: { ...crmConfig.sf, client_id: e.target.value } })} disabled={!crmConfig.enabled} data-testid="sf-client-id" />
                <Input className="rounded-none" type="password" placeholder="Consumer Secret (client_secret)" value={crmConfig.sf.client_secret} onChange={(e) => setCrmConfig({ ...crmConfig, sf: { ...crmConfig.sf, client_secret: e.target.value } })} disabled={!crmConfig.enabled} data-testid="sf-client-secret" />
                <Input className="rounded-none" placeholder="Username" value={crmConfig.sf.username} onChange={(e) => setCrmConfig({ ...crmConfig, sf: { ...crmConfig.sf, username: e.target.value } })} disabled={!crmConfig.enabled} data-testid="sf-username" />
                <Input className="rounded-none" type="password" placeholder="Password" value={crmConfig.sf.password} onChange={(e) => setCrmConfig({ ...crmConfig, sf: { ...crmConfig.sf, password: e.target.value } })} disabled={!crmConfig.enabled} data-testid="sf-password" />
                <Input className="rounded-none" type="password" placeholder="Security Token" value={crmConfig.sf.security_token} onChange={(e) => setCrmConfig({ ...crmConfig, sf: { ...crmConfig.sf, security_token: e.target.value } })} disabled={!crmConfig.enabled} data-testid="sf-security-token" />
                <Input className="rounded-none" placeholder="API Version (default v60.0)" value={crmConfig.sf.api_version} onChange={(e) => setCrmConfig({ ...crmConfig, sf: { ...crmConfig.sf, api_version: e.target.value } })} disabled={!crmConfig.enabled} data-testid="sf-api-version" />
                <p className="text-[9px] text-[#9CA3AF]">Use a Connected App with API + refresh_token scopes. Security token comes from Setup → My Personal Info → Reset Security Token.</p>
              </div>
            )}

            <p className="text-[10px] text-[#9CA3AF] italic">
              Secrets are stored encrypted-at-rest in MongoDB and never returned to the UI after save. Re-enter them whenever you rotate keys.
            </p>

            <DialogFooter className="flex justify-between gap-2">
              <Button
                variant="outline"
                className="rounded-none text-xs font-bold tracking-[0.1em] uppercase"
                onClick={testCrm}
                disabled={!crmConfig.enabled || crmTesting || !crmSaved}
                title={!crmSaved ? "Save first, then test" : "Verify auth with the CRM"}
                data-testid="crm-test-btn"
              >
                {crmTesting ? <AlertCircle size={12} className="mr-1 animate-pulse" /> : <CheckCircle2 size={12} className="mr-1" />}
                Test Connection
              </Button>
              <Button
                className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-bold tracking-[0.1em] uppercase"
                onClick={saveCrm}
                data-testid="crm-save-btn"
              >
                Save CRM Config
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
